import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch, apiPost } from "@vimaana/ui";

import type { CatalogPosition } from "../lib/invoices";

/**
 * Pflanzenbibliothek (hetzner/webhook/kontor_pflanzen.py, Migration 220).
 *
 * Benjamin, 16.10.2026: zuerst für Angebote — botanischer Name, Qualität und Lieferantenpreis
 * direkt in der Position; Aufschlag je Gruppe, im Angebot je Pflanze änderbar.
 */
export type PflanzenTyp = "staude" | "gras" | "farn" | "kraut" | "zwiebel" | "wasser" | "saisonal" | "kuebel"
  | "strauch" | "rose" | "kletter" | "obst" | "baum";
export type PflanzenStatus = "vorschlag" | "geprueft" | "verworfen";

/** Ein Treffer fürs Titelfeld — eine Katalogposition plus das, woraus ihr Preis kam. */
export type PflanzeTreffer = CatalogPosition & {
  einkauf_netto: number | null;
  aufschlag: number;
  lieferant: string;
  pflanze_id: string;
  qualitaet_id: string;
  geprueft: boolean;
  typ: string;
};

export type PflanzeZeile = {
  id: string;
  botanisch: string;
  gattung: string;
  deutsch: string;
  synonyme: string[];
  typ: PflanzenTyp;
  hoehe_von: number | null;
  hoehe_bis: number | null;
  breite_von: number | null;
  breite_bis: number | null;
  standort: string[];
  feuchte: string[];
  bluete_von: number | null;
  bluete_bis: number | null;
  bluete_farbe: string;
  heimisch: boolean | null;
  insektenfreundlich: boolean | null;
  status: PflanzenStatus;
  updated_at: string;
  qualitaeten: number;
  ek_ab: number | null;
};

export type PflanzeLieferant = { id: string; name: string; preis_netto: number | null; preis_am: string; artikelnummer: string };
export type PflanzeQualitaet = { id: string; bezeichnung: string; material_id: string; material: string; lieferanten: PflanzeLieferant[] };

export type PflanzeBlatt = Omit<PflanzeZeile, "qualitaeten" | "ek_ab"> & {
  handelsnamen: string[];
  boden: string;
  immergruen: boolean | null;
  giftig: boolean | null;
  winterhaerte: string;
  stueck_je_m2: number | null;
  hinweis: string;
  quellen: { titel: string; url: string }[];
  aus_wissen: string[];
  geprueft_von: string;
  geprueft_am: string;
  angelegt_von: string;
  aufschlag: number;
  qualitaeten: PflanzeQualitaet[];
  /** Letzte Recherche durch Claude auf dem Evo (evo/pflanzen-recherche.py) */
  recherche: { zustand: "laeuft" | "fertig" | "fehler"; fehler: string; zeit: string } | null;
};

export type PflanzeAenderung = Partial<Pick<PflanzeBlatt,
  "botanisch" | "deutsch" | "synonyme" | "typ" | "hoehe_von" | "hoehe_bis" | "breite_von" | "breite_bis" | "standort"
  | "boden" | "feuchte" | "bluete_von" | "bluete_bis" | "bluete_farbe" | "heimisch" | "insektenfreundlich"
  | "immergruen" | "giftig" | "winterhaerte" | "stueck_je_m2" | "hinweis">>;

export type PflanzenTypZeile = { typ: PflanzenTyp; name: string; aufschlag: number };

/** Titelfeld im Angebot: Pflanzen ab drei Zeichen. */
export function usePflanzenAngebot(q: string, aktiv: boolean) {
  return useQuery<{ items: PflanzeTreffer[] }>({
    queryKey: ["pflanzen", "angebot", q],
    queryFn: () => apiGet(`/api/kontor/pflanzen/angebot?q=${encodeURIComponent(q)}&limit=8`),
    enabled: aktiv && q.trim().length >= 3,
    staleTime: 60_000,
  });
}

export function usePflanzenListe(q: string, status: string, typ: string) {
  const p = new URLSearchParams({ q, status, typ });
  return useQuery<PflanzeZeile[]>({
    queryKey: ["pflanzen", "liste", q, status, typ],
    queryFn: () => apiGet(`/api/kontor/pflanzen?${p.toString()}`),
    staleTime: 30_000,
  });
}

export function usePflanzeBlatt(id: string) {
  return useQuery<PflanzeBlatt>({
    queryKey: ["pflanzen", "blatt", id],
    queryFn: () => apiGet(`/api/kontor/pflanzen/${id}`),
    enabled: Boolean(id),
    // Solange Claude recherchiert, alle 15 s nachsehen — das Ergebnis übernimmt der Server beim Lesen.
    refetchInterval: (q) => (q.state.data?.recherche?.zustand === "laeuft" ? 15_000 : false),
  });
}

export function usePflanzeRecherche(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<PflanzeBlatt>(`/api/kontor/pflanzen/${id}/recherche`, {}),
    onSuccess: (blatt) => qc.setQueryData(["pflanzen", "blatt", id], blatt),
  });
}

export function usePflanzenTypen() {
  return useQuery<PflanzenTypZeile[]>({
    queryKey: ["pflanzen", "typen"],
    queryFn: () => apiGet("/api/kontor/pflanzen/typen"),
    staleTime: 5 * 60_000,
  });
}

export function usePflanzeAendern(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (werte: PflanzeAenderung) => apiPatch<PflanzeBlatt>(`/api/kontor/pflanzen/${id}`, werte),
    onSuccess: (blatt) => {
      qc.setQueryData(["pflanzen", "blatt", id], blatt);
      qc.invalidateQueries({ queryKey: ["pflanzen", "liste"] });
    },
  });
}

export function usePflanzeStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: PflanzenStatus) => apiPost<PflanzeBlatt>(`/api/kontor/pflanzen/${id}/status`, { status }),
    onSuccess: (blatt) => {
      qc.setQueryData(["pflanzen", "blatt", id], blatt);
      qc.invalidateQueries({ queryKey: ["pflanzen", "liste"] });
    },
  });
}

export function useAufschlagSetzen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ typ, faktor }: { typ: string; faktor: number }) =>
      apiPost<PflanzenTypZeile[]>("/api/kontor/pflanzen/aufschlag", { typ, faktor }),
    onSuccess: (typen) => {
      qc.setQueryData(["pflanzen", "typen"], typen);
      qc.invalidateQueries({ queryKey: ["pflanzen", "angebot"] });
    },
  });
}
